import { useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { BUSINESS } from "../../lib/api";
import { Reveal } from "../Reveal";
import { Car, ArrowRight, MapPin, BadgeCheck, BusFront, Lock } from "lucide-react";

export const CarRental = () => {
  const { t } = useLang();
  const [hasBookedCar, setHasBookedCar] = useState(false);
  return (
    <section id="carrental" className="scroll-mt-24 bg-[#FAF8F5] px-4 sm:px-8 pb-20 lg:pb-28">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#C85A32] to-[#8C3214] p-8 sm:p-12">
            <div className="pointer-events-none absolute -right-8 -top-10 opacity-10">
              <Car className="h-56 w-56 text-white" />
            </div>
            <div className="relative flex flex-col items-start gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="max-w-xl">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white backdrop-blur-sm">
                    <Car className="h-4 w-4" /> {t("carrental.eyebrow")}
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#D9A05B] px-3 py-1 text-xs font-semibold text-[#3a1a0b]" data-testid="car-rental-partner">
                    <BadgeCheck className="h-4 w-4" /> {t("carrental.partner")}
                  </span>
                </div>
                <h3 className="mt-4 font-serif text-3xl sm:text-4xl leading-tight text-white">{t("carrental.title")}</h3>
                <p className="mt-3 text-base sm:text-lg leading-relaxed text-white/90">{t("carrental.subtitle")}</p>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-white/80">{t("carrental.pickupIntro")}</span>
                  {t("carrental.pickup").map((p) => (
                    <span key={p} className="inline-flex items-center gap-1.5 rounded-full border border-white/30 bg-white/10 px-3 py-1.5 text-sm font-medium text-white backdrop-blur-sm">
                      <MapPin className="h-3.5 w-3.5" /> {p}
                    </span>
                  ))}
                </div>
                <div className="mt-4 flex items-start gap-2.5 rounded-2xl bg-white/10 p-4 backdrop-blur-sm" data-testid="car-rental-shuttle">
                  <BusFront className="mt-0.5 h-5 w-5 shrink-0 text-[#F3D9B8]" />
                  <p className="text-sm leading-relaxed text-white/95">{t("carrental.shuttle")}</p>
                </div>
              </div>
              <div className="flex w-full shrink-0 flex-col gap-3 lg:w-auto">
                <a
                  data-testid="car-rental-button"
                  href={BUSINESS.carRental}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setHasBookedCar(true)}
                  className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-white px-8 py-4 text-base font-semibold text-[#8C3214] shadow-lg transition-transform duration-300 hover:scale-105"
                >
                  {t("carrental.cta")}
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </a>

                {hasBookedCar ? (
                  <a
                    data-testid="shuttle-request-button"
                    href={`https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(t("carrental.shuttleMsg"))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2.5 rounded-full border border-white/50 bg-white/10 px-8 py-4 text-base font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20"
                  >
                    <BusFront className="h-5 w-5" />
                    {t("carrental.shuttleCta")}
                  </a>
                ) : (
                  <button
                    type="button"
                    disabled
                    aria-disabled="true"
                    data-testid="shuttle-request-button-disabled"
                    title={t("carrental.shuttleLockedNote")}
                    className="inline-flex cursor-not-allowed items-center justify-center gap-2.5 rounded-full border border-white/20 bg-white/5 px-8 py-4 text-base font-semibold text-white/50"
                  >
                    <Lock className="h-4 w-4" />
                    {t("carrental.shuttleCta")}
                  </button>
                )}

                {!hasBookedCar && (
                  <div
                    data-testid="shuttle-locked-banner"
                    className="flex items-start gap-2 rounded-xl border border-white/20 bg-black/15 px-4 py-2.5 text-xs leading-relaxed text-white/85 backdrop-blur-sm"
                  >
                    <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#F3D9B8]" />
                    <span>{t("carrental.shuttleLockedNote")}</span>
                  </div>
                )}

                <a
                  data-testid="car-rental-view-link"
                  href={`mailto:${BUSINESS.carRentalEmail}?subject=${encodeURIComponent(t("carrental.emailSubject"))}&body=${encodeURIComponent(t("carrental.emailBody"))}`}
                  className="inline-flex items-center justify-center gap-1.5 text-sm font-medium text-white/85 underline-offset-4 hover:underline"
                >
                  {t("carrental.emailCta")} <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
